import React, { useState } from 'react';
import { motion } from 'framer-motion';
interface ChartData {
  label: string;
  value: number;
  color: string;
}
interface RingChartProps {
  data: ChartData[];
  title: string;
  activeSegment: string | null;
  onSegmentHover: (segment: string | null) => void;
  width?: number;
  height?: number;
}
export const RingChart: React.FC<RingChartProps> = ({
  data,
  title,
  activeSegment,
  onSegmentHover,
  width = 200,
  height = 200
}) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const radius = Math.min(width, height) / 3;
  const strokeWidth = radius * 0.5;
  const center = {
    x: width / 2,
    y: height / 2
  };
  let currentAngle = -Math.PI / 2;
  const segments = data.map((item, index) => {
    const startAngle = currentAngle;
    const angle = 2 * Math.PI * item.value / total;
    currentAngle += angle;
    const isActive = activeSegment === item.label;
    const separation = isActive ? 10 : 0;
    const x1 = center.x + (radius + separation) * Math.cos(startAngle);
    const y1 = center.y + (radius + separation) * Math.sin(startAngle);
    const x2 = center.x + (radius + separation) * Math.cos(currentAngle);
    const y2 = center.y + (radius + separation) * Math.sin(currentAngle);
    const largeArcFlag = angle <= Math.PI ? '0' : '1';
    const path = `
      M ${x1} ${y1}
      A ${radius + separation} ${radius + separation} 0 ${largeArcFlag} 1 ${x2} ${y2}
    `;
    return {
      path,
      color: item.color,
      label: item.label,
      value: item.value,
      startAngle,
      endAngle: currentAngle
    };
  });
  return <div className="flex flex-col items-center" data-id="element-306" data-mp="3ds3our5vv3al">
      <h3 className="text-sm font-medium text-[#141617] mb-4" data-id="element-307" data-mp="3dqadiykbruw9">{title}</h3>
      <div className="relative" style={{
      width,
      height
    }} data-id="element-308" data-mp="3iqy3ri0qc7pf">
        <svg width={width} height={height} data-id="element-309" data-mp="1jf43ehetneve">
          {segments.map((segment, index) => <g key={index} data-id="element-310" data-mp="1od5hsp1ayq0j" data-mpi={index}>
              <motion.path d={segment.path} fill="none" strokeWidth={strokeWidth} stroke={segment.color} strokeLinecap="round" initial={{
            pathLength: 0
          }} animate={{
            pathLength: 1,
            opacity: activeSegment === null || activeSegment === segment.label ? 1 : 0.5
          }} transition={{
            duration: 1,
            ease: 'easeInOut',
            delay: index * 0.2
          }} onMouseEnter={() => onSegmentHover(segment.label)} onMouseLeave={() => onSegmentHover(null)} onClick={() => onSegmentHover(activeSegment === segment.label ? null : segment.label)} style={{
            cursor: 'pointer',
            transition: 'opacity 0.2s ease'
          }} whileHover={{
            scale: 1.05
          }} data-id="element-311" data-mp="1oeglypyntzs6" />
            </g>)}
        </svg>
        <motion.div className="absolute inset-0 flex items-center justify-center" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.3
      }} data-id="element-312" data-mp="2421zjaoqqrgc">
          <div className="text-center" data-id="element-313" data-mp="2e1j23j8p4p83">
            <motion.div initial={{
            scale: 0.9,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1,
            transition: {
              duration: 0.2
            }
          }} key={activeSegment || 'total'} // Force animation on segment change
          data-id="element-314" data-mp="2dyeu0as419c5">
              {activeSegment ? <div className="text-sm" data-id="element-315" data-mp="2smyets1jpgn5">
                  <div className="font-medium text-[#141617]" data-id="element-316" data-mp="2sln6rg12hmq8">
                    {activeSegment}
                  </div>
                  <div className="text-[#707070]" data-id="element-317" data-mp="2dw7kodche5gv">
                    {data.find(d => d.label === activeSegment)?.value.toFixed(1)}
                    %
                  </div>
                </div> : <div className="text-sm" data-id="element-318" data-mp="24zgps49o0wnv">
                  <div className="font-medium text-[#707070]" data-id="element-319" data-mp="28yxuuwl6jrsi">Total</div>
                  <div className="text-[#141617]" data-id="element-320" data-mp="28wux1qdscud8">{total.toFixed(1)}%</div>
                </div>}
            </motion.div>
          </div>
        </motion.div>
      </div>
      <div className="flex flex-wrap justify-center gap-3 mt-4" data-id="element-321" data-mp="1zx7xzb8j40nc">
        {data.map((item, index) => <div key={index} className="flex items-center gap-2 cursor-pointer transition-opacity duration-200" style={{
        opacity: activeSegment === null || activeSegment === item.label ? 1 : 0.5
      }} onMouseEnter={() => onSegmentHover(item.label)} onMouseLeave={() => onSegmentHover(null)} data-id="element-322" data-mp="32anm5ppr70s6" data-mpi={index}>
            <div className="w-3 h-3 rounded-full" style={{
          backgroundColor: item.color
        }} data-id="element-323" data-mp="2ojr8d50hko56" />
            <span className="text-xs text-[#707070]" data-id="element-324" data-mp="2jn0rifguxoip">
              {item.label} ({item.value}%)
            </span>
          </div>)}
      </div>
    </div>;
};